import { useEffect, useRef, type RefObject } from "react";
import { rememberOfferClick } from "@/lib/offers";
import { siteEvents } from "@/lib/siteEvents";

/**
 * Funnel events for an offer placement, all site_events "act" rows with
 * { key: <offer id>, surface: <placement> } plus an optional A/B `variant`:
 *   offer_view      once, when at least half of the element has been on screen
 *   offer_click     on the link or button (also remembered for the visit, so
 *                   checkout_start on the destination page can name the placement)
 *   checkout_start  fired by the checkout buttons themselves (LibraryUI
 *                   BuyButton, /playbook, /sprint), with from_key and
 *                   from_surface from the last offer_click
 */
export function useOfferView(
  ref: RefObject<HTMLElement>,
  key: string | null | undefined,
  surface: string,
  variant?: string,
) {
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !key) return;
    const id = `${key}|${surface}|${variant ?? ""}`;
    if (seen.current === id) return;
    const fire = () => {
      if (seen.current === id) return;
      seen.current = id;
      siteEvents.act("offer_view", { key, surface, variant });
    };
    if (typeof IntersectionObserver === "undefined") { fire(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { fire(); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, key, surface, variant]);
}

export function trackOfferClick(key: string, surface: string, variant?: string) {
  rememberOfferClick(key, surface, variant);
  siteEvents.act("offer_click", { key, surface, variant });
}
